import { ControlType, FieldFilterType, Property, PropertyType } from '../../../base/types';
import React, { FC, useCallback, useEffect, useState } from 'react';

import clsx from 'clsx';
import { useAuth } from '../../../../modules/auth';
import { useLazyReadMultipleQuery } from '../../../services/data/tableDataApi';
import Marquee from "react-fast-marquee";

import moment from 'moment';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, useMediaQuery, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import MyMarquee from './Marqee';
import { IControlAttributes } from '../../../base/models';
import { Registry } from '../../../base/register';

export const useGetNotificationEvents = () => {
    const { user, siteId } = useAuth();
    const [getRecords] = useLazyReadMultipleQuery();
    const [notificationIds, setNotificationIds] = useState<string[]>([]);
    const today = moment().format("YYYY-MM-DD");

    useEffect(() => {
        getNotificationIds();
        // eslint-disable-next-line
    }, [user]);

    const getNotificationIds = (): Promise<string[]> => {

        return getRecords({
            userId: user?.entityId || user?.contactId,
            portalId: siteId || '',
            tableId: "slcm_notificationevent",
            fieldList: ["slcm_startdate", "slcm_enddate", "slcm_notification", "slcm_student"],
            filters: [{
                group: 0, filters: [
                    { field: "slcm_student", type: FieldFilterType.Const, value: `${user?.entityId || user?.contactId}` },
                    { field: "slcm_startdate", type: FieldFilterType.Filter, value: `<= '${today}'` },
                    { field: "slcm_enddate", type: FieldFilterType.Filter, value: `>= '${today}'` }
                ]
            }]
        })
            .unwrap()
            .then((data) => {
                if (data.records.length > 0) {
                    const ids = data.records.map(record => record["slcm_notification"]);
                    setNotificationIds(ids);
                    return Promise.resolve(ids);
                }

                return Promise.reject('No active notifications found.');
            });
    };

    return { notificationIds };
}

interface INotification {
    name?: string,
    message?: string,
    notificationid?: string,
}

export const useNotification = (notificationIds?: string[]) => {
    const { user, siteId } = useAuth();
    const [getRecords] = useLazyReadMultipleQuery();
    const [records, setRecords] = useState<INotification[]>([]);

    useEffect(() => {
        if (!notificationIds || notificationIds.length === 0) return;
        getNotifations(notificationIds);
        // eslint-disable-next-line
    }, [user, notificationIds]);

    const getNotifations = (notificationIds: string[]): Promise<INotification[]> => {
        if (!user?.contactId) {
            return Promise.reject('User contact not found.');
        }

        const fetchRecordsPromises = notificationIds.map(feedbackId =>
            getRecords({
                userId: user?.userId || '',
                portalId: siteId || '',
                tableId: "slcm_notification",
                fieldList: ["slcm_name", "slcm_message", "slcm_notificationid"],
                filters: [{
                    group: 0, filters: [
                        { field: "slcm_notificationid", type: FieldFilterType.Const, value: feedbackId },
                    ]
                }]
            })
                .unwrap()
        );

        return Promise.all(fetchRecordsPromises)
            .then(results => {
                const combinedRecords = results.flatMap(data =>
                    data.records.map<INotification>(p => ({
                        name: p["slcm_name"],
                        message: p["slcm_message"],
                        notificationid: p["slcm_notificationid"],
                    }))
                );

                setRecords(combinedRecords);
                return Promise.resolve(combinedRecords);
            });
    };

    return { records };
};

const NotificationMessage = ({ selectedMessage, open, handleClose }: { selectedMessage: string, open: boolean, handleClose: () => void }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
            sx={{
                '& .MuiDialog-paper': {
                    minWidth: isMobile ? '90%' : '700px',
                    maxWidth: isMobile ? '90%' : '700px',
                    margin: isMobile ? 0 : undefined,
                    borderRadius: isMobile ? 0 : undefined,
                }
            }}
        >
            <DialogTitle id="responsive-dialog-title">
                <h4>Notification Message</h4>
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <div
                        dangerouslySetInnerHTML={{ __html: selectedMessage }}
                    />
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} autoFocus>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const MarqeeComponent: FC<IControlAttributes> = ({
    control, className
}) => {
    const { notificationIds } = useGetNotificationEvents();
    const { records } = useNotification(notificationIds);
    const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleTextClick = (notificationid: string) => {
        const record = records.find(record => record.notificationid === notificationid);
        if (record) {
            setSelectedMessage(record.message || '');
            setDialogOpen(true);
        }
    };

    const handleClose = () => {
        setDialogOpen(false);
    };

    const renderMarquee = useCallback(() => {
        const texts = records.map(record => ({ name: record.name || "", notificationid: record.notificationid || "" }));

        return (
            <Marquee speed={40} direction='left' pauseOnHover={true}>
                <MyMarquee
                    texts={texts}
                    onTextClick={handleTextClick}
                />
            </Marquee>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [records]);

    return (
        <div className={clsx(className)}>
            {renderMarquee()}
            {selectedMessage && <NotificationMessage selectedMessage={selectedMessage} open={dialogOpen} handleClose={handleClose} />}
        </div>
    );
};

Registry.addControl({
    id: "c910c14c-5992-47e6-81fb-e1ee5c4ebce6",
    name: "Marqee",
    type: ControlType.Marquee,
    properties: [
        { id: Property.Label, name: 'label', type: PropertyType.String },
    ],
    component: MarqeeComponent,
})
